import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  Medication as MedicationIcon,
  Photo as RxImagesIcon,
  NoteAlt as NotesIcon,
  Timeline as TimelineIcon,
  History as HistoryIcon,
  Verified as VerifiedIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import api from '../../services/api';
import { usePharmacyStore } from '../../stores/pharmacyStore';
import { useCurrentPatientContext } from '../../hooks/useCurrentPatientContext';

// Import the tab content components
import { AllPrescriptions } from '../../pages/patient/Patient360/tabs/AllPrescriptions';
import { NotesAndDocuments } from '../../pages/patient/Patient360/tabs/NotesAndDocuments';
import OrderTimelineView from '../orders/OrderTimelineView';
import { OrdersTable } from '../patient-360/tables/OrdersTable';
import EmbeddedOrderDetailView from './EmbeddedOrderDetailView';
import { RxImageViewer } from '../patient-360/rx/RxImageViewer';
import VerificationTab from '../patient-360/VerificationTab';

export type PatientInfoTab = 'prescriptions' | 'images' | 'notes' | 'timeline' | 'history' | 'verification';

interface UnifiedPatientInfoDialogProps {
  open: boolean;
  onClose: () => void;
  initialTab?: PatientInfoTab;
  // Patient context
  patientId: string;
  patientName: string;
  pharmacyId: string;
  // Order context (for timeline, history highlighting)
  orderId?: string;
  orderNumber?: string;
  billingItemId?: string;
  highlightRxNumbers?: string[];
}

const TAB_INDICES: Record<PatientInfoTab, number> = {
  prescriptions: 0,
  images: 1,
  notes: 2,
  timeline: 3,
  history: 4,
  verification: 5,
};

const INDEX_TO_TAB: PatientInfoTab[] = ['prescriptions', 'images', 'notes', 'timeline', 'history', 'verification'];

/**
 * UnifiedPatientInfoDialog - Single tabbed dialog consolidating patient information
 *
 * Replaces 5 separate dialogs (All Rx, Rx Images, Notes, Timeline, History)
 * with one unified experience. When viewing order history, clicking an order
 * shows details within the dialog instead of navigating away.
 *
 * Features:
 * - Tabbed interface with All Rx, Rx Images, Notes, Timeline, History
 * - Embedded order detail view in History tab (no navigation)
 * - Remembers last viewed tab within session
 */
const UnifiedPatientInfoDialog: React.FC<UnifiedPatientInfoDialogProps> = ({
  open,
  onClose,
  initialTab = 'prescriptions',
  patientId,
  patientName,
  pharmacyId,
  orderId,
  orderNumber,
  billingItemId,
  highlightRxNumbers = [],
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const activePharmacy = usePharmacyStore((state: any) => state.activePharmacy);
  const effectivePharmacyId = pharmacyId || activePharmacy?.id;

  // PRX-85: top bar recent-patients trigger, only while this dialog is open.
  // The dialog can stay mounted while closed, so gate on `open` rather than
  // relying on unmount to restore the outer patient.
  const patientCompositeId = patientId && effectivePharmacyId
    ? (patientId.includes('_') ? patientId : `${effectivePharmacyId}_${patientId}`)
    : null;
  useCurrentPatientContext(
    open && patientCompositeId && patientName
      ? { compositeId: patientCompositeId, pharmacyId: effectivePharmacyId, displayName: patientName }
      : undefined,
  );

  // Tab state
  const [activeTab, setActiveTab] = useState(TAB_INDICES[initialTab] || 0);

  // History tab state - for embedded order detail view
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [historyOrders, setHistoryOrders] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Reset tab when initialTab changes or dialog opens
  useEffect(() => {
    if (open) {
      setActiveTab(TAB_INDICES[initialTab] || 0);
      setSelectedOrder(null); // Reset order detail view
    }
  }, [open, initialTab]);

  // Fetch history orders when History tab is active
  useEffect(() => {
    if (open && activeTab === TAB_INDICES.history && patientId && effectivePharmacyId) {
      fetchHistoryOrders();
    }
  }, [open, activeTab, patientId, effectivePharmacyId]);

  const fetchHistoryOrders = async () => {
    if (!patientId || !effectivePharmacyId) return;

    setHistoryLoading(true);
    try {
      const response = await api.get(`/api/orders/${effectivePharmacyId}`, {
        params: {
          patientId: patientId,
          limit: 100,
        },
      });
      const fetchedOrders = response.data.data || response.data || [];
      setHistoryOrders(fetchedOrders);
    } catch (error: any) {
      console.error('Failed to fetch patient orders:', error);
      enqueueSnackbar('Failed to load order history', { variant: 'error' });
      setHistoryOrders([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    // Clear order detail when switching away from History tab
    if (newValue !== TAB_INDICES.history) {
      setSelectedOrder(null);
    }
  };

  const handleOrderSelect = (order: any) => {
    setSelectedOrder(order);
  };

  const handleBackToHistory = () => {
    setSelectedOrder(null);
  };

  // Enhance orders with highlight flag
  const ordersWithHighlight = historyOrders.map((order) => {
    const hasHighlightedRx =
      highlightRxNumbers.length > 0 &&
      order.items?.some((item: any) => highlightRxNumbers.includes(item.rxNumber));
    return {
      ...order,
      _highlighted: hasHighlightedRx,
    };
  });

  const renderTabContent = () => {
    switch (INDEX_TO_TAB[activeTab]) {
      case 'prescriptions':
        return <AllPrescriptions patientId={patientId} />;

      case 'images':
        return (
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <RxImageViewer patientId={patientId} initialFilter="all" />
          </Box>
        );

      case 'notes':
        return <NotesAndDocuments patientId={patientId} />;

      case 'timeline':
        return orderId && effectivePharmacyId ? (
          <OrderTimelineView
            pharmacyId={effectivePharmacyId}
            orderId={orderId}
            billingItemId={billingItemId}
          />
        ) : (
          <Alert severity="info">No order selected for timeline view</Alert>
        );

      case 'history':
        // Show order detail or orders table
        if (selectedOrder) {
          return (
            <EmbeddedOrderDetailView
              orderId={selectedOrder.id}
              pharmacyId={effectivePharmacyId}
              onBack={handleBackToHistory}
            />
          );
        }

        return historyLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
            <CircularProgress />
          </Box>
        ) : historyOrders.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              No orders found for this patient
            </Typography>
          </Box>
        ) : (
          <Box sx={{ height: '100%', overflow: 'auto' }}>
            <OrdersTable
              orders={ordersWithHighlight}
              loading={false}
              pharmacyId={effectivePharmacyId}
              embedded={true}
              onOrderSelect={handleOrderSelect}
              onRefresh={fetchHistoryOrders}
            />
          </Box>
        );

      case 'verification':
        return orderId && effectivePharmacyId ? (
          <VerificationTab
            orderId={orderId}
            orderNumber={orderNumber}
            pharmacyId={effectivePharmacyId}
            patientId={patientId}
          />
        ) : (
          <Alert severity="info">No order selected for verification view</Alert>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
    >
      {/* Dialog Header */}
      <DialogTitle
        sx={{
          bgcolor: '#2c3e50',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 1.5,
        }}
      >
        <Box>
          <Typography variant="h6" component="div">
            Patient Info - {patientName}
          </Typography>
          {orderNumber && (
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              Order #{orderNumber}
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              minHeight: 48,
            },
          }}
        >
          <Tab
            icon={<MedicationIcon fontSize="small" />}
            label="All Rx"
            iconPosition="start"
          />
          <Tab
            icon={<RxImagesIcon fontSize="small" />}
            label="Rx Images"
            iconPosition="start"
          />
          <Tab
            icon={<NotesIcon fontSize="small" />}
            label="Notes"
            iconPosition="start"
          />
          <Tab
            icon={<TimelineIcon fontSize="small" />}
            label="Timeline"
            iconPosition="start"
            disabled={!orderId}
          />
          <Tab
            icon={<HistoryIcon fontSize="small" />}
            label="History"
            iconPosition="start"
          />
          <Tab
            icon={<VerifiedIcon fontSize="small" />}
            label="Verification"
            iconPosition="start"
            disabled={!orderId}
          />
        </Tabs>
      </Box>

      {/* Tab Content */}
      <DialogContent
        dividers
        sx={{
          p: 2,
          height: 'calc(100% - 120px)',
          overflow: 'auto',
        }}
      >
        {renderTabContent()}
      </DialogContent>
    </Dialog>
  );
};

export default UnifiedPatientInfoDialog;
